/**
 * "Klaviyo newsletter" picker on a newsletter placement (Results panel).
 * Linking the placement to the campaign that went out copies recipients /
 * opens / clicks into the placement's results, so the lookbook shows them.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { fmtShort } from '../../lib/dates';
import { CampaignAsset } from '../../lib/types';
import { useKlaviyoCampaigns, useKlaviyoLink } from '../../lib/api/assets';
import { errorDetail } from '../../lib/api/shopBanners';

interface Props {
  asset: CampaignAsset;
  /** Called with the placement as saved after a link / refresh, so the form
   *  shows the fetched numbers (and doesn't overwrite them on Save). */
  onLinked: (asset: CampaignAsset) => void;
}

const KlaviyoLink = ({ asset: initial, onLinked }: Props) => {
  // Kept here after a link / refresh: the form's own `asset` prop stays as
  // opened, so its other fields aren't reset.
  const [asset, setAsset] = useState(initial);
  const changed = useRef(false);
  const qc = useQueryClient();
  useEffect(() => () => {
    if (changed.current) qc.invalidateQueries({ queryKey: ['assets'] });
  }, [qc]);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const link = useKlaviyoLink();
  const list = useKlaviyoCampaigns(query, picking);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const linked = asset.klaviyo_campaign_id;

  const run = (campaignId: string | null) => {
    setMessage(null);
    link.mutate({ id: asset.id, campaignId }, {
      onSuccess: (saved) => {
        changed.current = true;
        setAsset(saved);
        onLinked(saved);
        setPicking(false);
        if (saved.warning) setMessage(saved.warning);
      },
      onError: (err) => setMessage(errorDetail(err) ?? 'Something went wrong with Klaviyo.'),
    });
  };

  const notConnected = (list.error as { response?: { status?: number } } | null)?.response?.status === 503;

  return (
    <div style={{ marginBottom: 12, fontSize: 12.5 }}>
      <div style={{ color: 'var(--ink-3)', fontWeight: 600, marginBottom: 6 }}>Klaviyo newsletter</div>
      {linked && !picking ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--ink)' }}>{asset.klaviyo_campaign_name || linked}</span>
          {asset.klaviyo_synced_at && (
            <span style={{ color: 'var(--ink-4)' }}>· numbers from {fmtShort(asset.klaviyo_synced_at.slice(0, 10))}</span>
          )}
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => run(linked)}>
            {link.isPending ? 'Fetching…' : 'Refresh'}
          </Button>
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => setPicking(true)}>
            Change
          </Button>
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => run(null)}>
            Unlink
          </Button>
        </div>
      ) : !picking ? (
        <Button type="button" variant="ghost" onClick={() => setPicking(true)}>
          Link to the Klaviyo newsletter
        </Button>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sent newsletters by name"
            />
            <Button type="button" variant="ghost" onClick={() => setPicking(false)}>Cancel</Button>
          </div>
          <div style={{
            marginTop: 6, maxHeight: 220, overflowY: 'auto',
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--bg-elevated)',
          }}>
            {list.isLoading && <div style={{ padding: 10, color: 'var(--ink-4)' }}>Loading newsletters…</div>}
            {notConnected && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>
                Klaviyo isn't connected to mosy yet — Geoffrey or Robbie need to add the Klaviyo key.
              </div>
            )}
            {list.error && !notConnected && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>
                {errorDetail(list.error) ?? 'Klaviyo could not be reached.'}
              </div>
            )}
            {list.data?.length === 0 && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>No sent newsletter matches.</div>
            )}
            {list.data?.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={link.isPending}
                onClick={() => run(c.id)}
                style={{
                  display: 'flex', justifyContent: 'space-between', gap: 10, width: '100%',
                  padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--border)',
                  background: c.id === linked ? 'var(--bg-subtle)' : 'transparent',
                  textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', fontSize: 12.5,
                }}
              >
                <span>{c.name}</span>
                <span style={{ color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
                  {c.send_time ? fmtShort(c.send_time.slice(0, 10)) : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      {message && <div style={{ marginTop: 6, color: 'var(--ink-3)' }}>{message}</div>}
    </div>
  );
};

export default KlaviyoLink;
